'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn, colSpanMap } from '../../../lib/utils'

import { WorkflowTile, WorkflowTileProps } from './tiles/WorkflowTile'
import { ContactTile, ContactTileProps } from './tiles/ContactTile'
import { ActionTile, ActionTileProps } from './tiles/ActionTile'
import { KanbanTile, type KanbanTileProps } from './tiles/KanbanTile'
import { AppTile, type AppTileProps } from './tiles/AppTile'
import { InfiniteScroll } from '../InfiniteScroll'
import { InputProps } from '../Input'
import { Button, ButtonProps } from '../Button'
import { DraggableProps } from '../DataView/Kanban'
import { CheckboxTile, type CheckboxTileProps } from './tiles/CheckboxTile'
import { TabsContent, TabsList, TabsTrigger, TabsWrapper } from '../Tabs'
import Icon, { iconTypes } from '../Icon'

const pageContentVariants = cva('h-full w-full')

type SpanCol = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12

type StyleProps = {
  id?: string
  className?: string
  span: SpanCol
  mdSpan: SpanCol
  lgSpan: SpanCol
  hidden?: boolean
}

export type ContactTileComponent = {
  component: 'ContactTile'
  props: ContactTileProps
} & StyleProps

export type ActionTileComponent = {
  component: 'ActionTile'
  props: ActionTileProps
} & StyleProps

export type WorkflowTileComponent = {
  component: 'WorkflowTile'
  props: WorkflowTileProps
} & StyleProps

export type AppTileComponent = {
  component: 'AppTile'
  props: AppTileProps
} & StyleProps

export type KanbanTileComponent = {
  component: 'KanbanTile'
  props: KanbanTileProps
} & StyleProps

export type CheckboxTileComponent = {
  component: 'CheckboxTile'
  props: CheckboxTileProps
} & StyleProps

export type ItemComponent =
  | ContactTileComponent
  | ActionTileComponent
  | WorkflowTileComponent
  | AppTileComponent
  | KanbanTileComponent
  | CheckboxTileComponent

export interface ListProps extends VariantProps<typeof pageContentVariants> {
  id?: string
  items: ItemComponent[]
  title: string
  /** Render `title` as a section heading above the grid. */
  showTitle?: boolean
  /** Spacing between tiled items. Default keeps existing flush lists. */
  gap?: 'none' | 'sm' | 'md'
  infiniteScroll?: {
    direction: 'up' | 'down'
    handleLoadMoreItems: () => void
    threshold?: number
    scrollContainer: HTMLElement | null
  }
  loadMore?: {
    handleLoadMoreItems: () => void
    threshold?: number
  }
  className?: string
  isSkeleton?: boolean
  search?: InputProps
  empty?: {
    enabled: boolean
    message: string
    action?: ButtonProps
  }
  tabs?: {
    value: string
    onValueChange: (value: string) => void | Promise<void>
    items: { label: string; value: string }[]
  }
  draggable?: DraggableProps
}

const listGapClass = {
  none: '',
  sm: 'gap-2',
  md: 'gap-3',
} as const

const ListItemLayout = ({
  items,
  draggable,
  id,
  gap = 'none',
}: {
  items: ListProps['items']
  draggable?: DraggableProps
  id?: string
  gap?: ListProps['gap']
}) => {
  // Handle undefined or empty items gracefully
  if (!items || items.length === 0) {
    return null
  }

  if (draggable) {
    // The y value is accumulated for each item according to it's height, increasing with the index.
    let y = 0
    // Deduplicate items by id to prevent React key warnings during drag operations
    const seenIds = new Set<string>()
    const uniqueItems = items.filter((item) => {
      const id = item.props.id
      if (!id || seenIds.has(id)) return false
      seenIds.add(id)
      return true
    })

    const itemsWithY = uniqueItems.map((item) => {
      const thisY = y
      const key = item.props.id ?? ''
      y += draggable.itemHeights?.[key] || 0
      return { ...item, y: thisY }
    })

    return (
      <>
        {itemsWithY.map((item, index) => (
          <div key={item.props.id}>
            {item.component === 'KanbanTile' && (
              <KanbanTile
                {...item.props}
                draggable={{
                  draggingItemId: item.id,
                  ...draggable,
                  dropZone: {
                    ...draggable.dropZone,
                    index,
                    y: item.y,
                  },
                  subsection: {
                    ...draggable.subsection,
                    id: draggable.subsection?.id,
                  },
                }}
              />
            )}
          </div>
        ))}
      </>
    )
  }

  return (
    <div className={cn('grid grid-cols-12', listGapClass[gap ?? 'none'])}>
      {items
        .filter((comp) => {
          if (comp.component === 'KanbanTile') {
            return true // KanbanTile doesn't have hidden property
          }
          return !!!comp.hidden
        })
        .map((field, i) => (
          <div
            className={cn(
              field.component === 'KanbanTile'
                ? 'col-span-12' // KanbanTile takes full width
                : cn(
                    colSpanMap[field.span].sm,
                    colSpanMap[field.mdSpan].md,
                    colSpanMap[field.lgSpan].lg,
                  ),
              'w-full',
              field.className,
            )}
            key={`${id}-field-${i}-${field.id}`}
          >
            {field.component === 'ContactTile' && (
              <ContactTile {...field.props} />
            )}
            {field.component === 'ActionTile' && (
              <ActionTile {...field.props} />
            )}
            {field.component === 'WorkflowTile' && (
              <WorkflowTile {...field.props} />
            )}
            {field.component === 'AppTile' && <AppTile {...field.props} />}
            {field.component === 'CheckboxTile' && (
              <CheckboxTile {...field.props} />
            )}
            {field.component === 'KanbanTile' && (
              <KanbanTile {...field.props} />
            )}
          </div>
        ))}
    </div>
  )
}

ListItemLayout.displayName = 'ListItemLayout'

const List = React.forwardRef<HTMLDivElement, ListProps>(
  (props: ListProps, ref) => {
    const {
      id,
      isSkeleton,
      infiniteScroll,
      title,
      showTitle,
      gap,
      search,
      empty,
      tabs,
      draggable,
    } = props

    const skeletonContact = () => {
      return (
        <div
          className={cn(
            'h-16 rounded-lg items-center p-2 flex flex-row gap-2 bg-accent animate-pulse',
          )}
          title={`contact-skeleton`}
        >
          <div className="flex flex-col justify-center overflow-hidden w-full" />
        </div>
      )
    }

    if (empty?.enabled) {
      return (
        <div className="flex h-full min-w-0 grow flex-col gap-2">
          {showTitle && title ? (
            <div className="flex items-baseline justify-between pb-1">
              <h3 className="text-sm font-medium tracking-tight text-foreground">
                {title}
              </h3>
            </div>
          ) : null}
          <div className="flex flex-col items-start gap-2 py-2 text-sm text-muted-foreground">
            {empty.message}
            {empty.action && <Button {...empty.action} />}
          </div>
        </div>
      )
    }

    const kanbanListHeight = draggable
      ? props.items.reduce((sum, item) => {
          const key = item.props.id ?? ''
          return sum + (draggable.itemHeights?.[key] || 0)
        }, 0)
      : 0

    const listContent = (
      <ListItemLayout
        items={props.items}
        draggable={draggable}
        id={id}
        gap={gap}
      />
    )

    const content = (
      <div className="flex h-full w-full min-w-0 grow flex-col">
        {isSkeleton && (
          <div className="flex w-full flex-col gap-2 py-2">
            {skeletonContact()}
            {skeletonContact()}
            {skeletonContact()}
          </div>
        )}

        {draggable ? (
          <div
            className="relative w-full min-w-0"
            style={{
              minHeight: kanbanListHeight > 0 ? kanbanListHeight : undefined,
            }}
          >
            {listContent}
          </div>
        ) : infiniteScroll ? (
          <InfiniteScroll {...infiniteScroll} id={title}>
            {listContent}
          </InfiniteScroll>
        ) : (
          listContent
        )}
      </div>
    )

    return (
      <div className="flex h-full min-w-0 grow flex-col gap-2">
        {showTitle && title ? (
          <div className="flex items-baseline justify-between pb-1">
            <h3 className="text-sm font-medium tracking-tight text-foreground">
              {title}
            </h3>
          </div>
        ) : null}
        {content}
      </div>
    )
  },
)

export { List }

List.displayName = 'List'
